import { FC } from "react";
import { useFragment } from "react-relay";
import { Link } from "react-router-dom";
import { graphql } from "relay-runtime";
import { Decimal, Fiat, IconDenom, formatApr, i18n } from "rujira.ui";
import { AngleRight } from "rujira.ui/src/components/icons/Icons";
import { Subscription } from "../../services/useNodeSubscription";
import { StakingPoolRowFragment$key } from "./__generated__/StakingPoolRowFragment.graphql";

const row = graphql`
  fragment StakingPoolRowFragment on StakingPool {
    id
    bondAsset {
      metadata {
        symbol
      }
    }
    deploymentStatus
    stakingStatus: status {
      accountBond
      liquidBondSize
      valueUsd
    }
    stakingSummary: summary {
      apr {
        value
        status
      }
    }
  }
`;

const subscription = graphql`
  subscription StakingPoolSubscription($id: ID!) {
    node(id: $id) {
      ... on StakingPool {
        status {
          accountBond
          liquidBondSize
          valueUsd
        }
        stakingSummary: summary {
          apr {
            value
            status
          }
        }
      }
    }
  }
`;

export const StakingPoolRow: FC<{ k: StakingPoolRowFragment$key }> = ({
  k,
}) => {
  const { id, bondAsset, stakingStatus, deploymentStatus, stakingSummary } =
    useFragment(row, k);
  const url = `/strategies/staking/${encodeURIComponent(bondAsset.metadata.symbol)}`;

  const label =
    bondAsset.metadata.symbol == "LP-X/RUJI-RUNE"
      ? "RUJI / RUNE CLP"
      : bondAsset.metadata.symbol;
  return (
    <tr className="pointer">
      <Subscription id={id} subscription={subscription} />
      <td>
        <Link
          to={url}
          className="flex ai-c mt-0.5 no-underline color-white w-full">
          <IconDenom denom={label || ""} className="w-3.5 mx-0.5" />
          <h3 className="condensed fs-22 lh-22 fs-lg-28 lh-lg-28 fw-400 mb-0 ml-1 nowrap">
            {label || ""}
          </h3>
        </Link>
      </td>
      {deploymentStatus === "LIVE" ? (
        <td>
          <Link to={url} className="flex dir-c no-underline color-white w-full">
            <Fiat
              amount={BigInt(stakingStatus?.valueUsd || 0)}
              symbol="$"
              decimals={8}
              className="fs-16 fs-md-18 color-white"
            />
            <div className="flex ai-c mt-0.5">
              <Decimal
                amount={
                  BigInt(stakingStatus?.accountBond || 0) +
                  BigInt(stakingStatus?.liquidBondSize || 0)
                }
                className="fs-12 fs-md-14 condensed color-grey"
                symbol={label}
              />
            </div>
          </Link>
        </td>
      ) : (
        <td className="text-right">
          <i18n.span className="tag inline-block">Preview only</i18n.span>
        </td>
      )}
      <td className="text-right">{formatApr(stakingSummary.apr)}</td>
      <td className="text-right">
        <Link to={url} className="block w-full text-right">
          <i18n.div className="tag tag--primary">Staking</i18n.div>
        </Link>
      </td>
      <td className="w-3">
        <Link to={url} className="block w-full text-right">
          <AngleRight className="w-3 h-3 color-grey" />
        </Link>
      </td>
    </tr>
  );
};
